package org.matthewo.pages.event.tabs

import js.objects.buildReadonlyRecord
import org.matthewo.MATCH_SEARCH
import org.matthewo.api.TeamRanking
import org.matthewo.js.toFixed
import org.matthewo.math.ifNaN
import org.matthewo.pages.event.EventTabFC
import org.matthewo.pages.team.TEAM_PARAM
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.router.RoutePath
import org.matthewo.routes.API
import org.matthewo.schema.CRI_YEAR_PREFIX
import org.matthewo.table.Column
import org.matthewo.table.Order
import org.matthewo.table.Table
import tanstack.react.router.useNavigate
import tanstack.react.router.useSearch
import web.cssom.NamedColor
import web.cssom.TextDecoration
import kotlin.math.roundToInt

private fun Double.fixedReplaceNaN(decimal: Int, replacement: String) = ifNaN(null)?.toFixed(decimal) ?: replacement
private fun Int.sortRank() = if (this < 1) null else this
private fun Int.displayRank() = if (this < 1) "-" else toFixed(0)

private val RANK_COLUMN = Column<TeamRanking>("Rank", { it.actual.rank.displayRank() }, compareBy { it.actual.rank.sortRank() }, Order.ASCENDING)
private val TEAM_COLUMN = Column<TeamRanking>("Team", { "${it.number} - ${it.name}" }, compareBy { it.number }, Order.ASCENDING) { textDecoration = TextDecoration.underline }
private val RS_COLUMN = Column<TeamRanking>("RS", { it.actual.rp.fixedReplaceNaN(2, "-") }, compareBy { it.actual.rp.ifNaN(null) }, Order.DESCENDING)
private val RP_COLUMN =
    Column<TeamRanking>("RP", { (it.actual.rp * it.actual.plays).ifNaN(0.0).roundToInt() }, compareBy { (it.actual.rp * it.actual.plays).ifNaN(null) }, Order.DESCENDING)
private val TBP1_COLUMN = Column<TeamRanking>("TBP1", { it.actual.tbp1.fixedReplaceNaN(2, "-") }, compareBy { it.actual.tbp1.ifNaN(null) }, Order.DESCENDING)
private val TBP2_COLUMN = Column<TeamRanking>("TBP2", { it.actual.tbp2.fixedReplaceNaN(2, "-") }, compareBy { it.actual.tbp2.ifNaN(null) }, Order.DESCENDING)
private val RECORD_COLUMN = Column<TeamRanking>("W-L-T", { "${it.actual.wins}-${it.actual.losses}-${it.actual.ties}" }, compareBy { it.actual.wins }, Order.DESCENDING)
private val MEAN_RANK_COLUMN = Column<TeamRanking>("Mean Rank", { it.projected?.meanRank?.toFixed(1) }, compareBy { it.projected?.meanRank ?: Double.MAX_VALUE }, Order.ASCENDING) {
    textDecoration = TextDecoration.underline
}
private val MIN_RANK_COLUMN = Column<TeamRanking>("Min Rank", { it.projected?.minRank?.toFixed(0) }, compareBy { it.projected?.minRank ?: Double.MAX_VALUE }, Order.ASCENDING) {
    if (it.projected?.minRank == it.projected?.maxRank) color = NamedColor.gray
}
private val MAX_RANK_COLUMN = Column<TeamRanking>("Max Rank", { it.projected?.maxRank?.toFixed(0) }, compareBy { it.projected?.maxRank ?: Double.MAX_VALUE }, Order.ASCENDING) {
    if (it.projected?.minRank == it.projected?.maxRank) color = NamedColor.gray
}
private val MEAN_RS_COLUMN = Column<TeamRanking>("Mean RS", { it.projected?.meanRP?.toFixed(2) }, compareBy { it.projected?.meanRP ?: 0 }, Order.DESCENDING)
private val MEAN_RP_COLUMN = Column<TeamRanking>("Mean RP", { it.projected?.meanRP?.toFixed(2) }, compareBy { it.projected?.meanRP ?: 0 }, Order.DESCENDING)
private val MEAN_TBP1_COLUMN = Column<TeamRanking>("Mean TBP1", { it.projected?.meanTBP1?.toFixed(2) }, compareBy { it.projected?.meanTBP1 ?: 0 }, Order.DESCENDING)

private val TB2_RANKINGS_TABLE = Table(
    pinHeader = true, pinFirstColumn = true, sortQuery = true, initialSortColumn = 0,
    RANK_COLUMN,
    TEAM_COLUMN,
    RS_COLUMN,
    TBP1_COLUMN,
    TBP2_COLUMN,
    RECORD_COLUMN,
)

private val TBP2_RANKINGS_TABLE_WITH_PROJECTIONS = Table(
    pinHeader = true, pinFirstColumn = true, sortQuery = true, initialSortColumn = 0,
    RANK_COLUMN,
    TEAM_COLUMN,
    RS_COLUMN,
    TBP1_COLUMN,
    TBP2_COLUMN,
    RECORD_COLUMN,
    MEAN_RANK_COLUMN,
    MIN_RANK_COLUMN,
    MAX_RANK_COLUMN,
    MEAN_RP_COLUMN,
    MEAN_TBP1_COLUMN,
)

private val RP_RANKINGS_TABLE_WITH_PROJECTIONS = Table(
    pinHeader = true, pinFirstColumn = true, sortQuery = true, initialSortColumn = 0,
    RANK_COLUMN,
    TEAM_COLUMN,
    RS_COLUMN,
    RP_COLUMN,
    TBP1_COLUMN,
    RECORD_COLUMN,
    MEAN_RANK_COLUMN,
    MIN_RANK_COLUMN,
    MAX_RANK_COLUMN,
    MEAN_RS_COLUMN,
    MEAN_TBP1_COLUMN,
)

val EventRankingsTable = EventTabFC<API.Event.Rankings, List<TeamRanking>>({ event, _, searchParams ->
    val match = searchParams[MATCH_SEARCH]?.toString()?.toIntOrNull()
    API.Event.Rankings(event, match)
}) { (coroutineScope, year, eventCode, data) ->
    val navigate = useNavigate()

    val searchParams = useSearch()
    val match = searchParams[MATCH_SEARCH]?.toString()?.toIntOrNull()

    if (null == data) return@EventTabFC

    val table = when {
        null == year || CRI_YEAR_PREFIX <= year -> TB2_RANKINGS_TABLE
        2025 <= year -> RP_RANKINGS_TABLE_WITH_PROJECTIONS
        2024 <= year -> TBP2_RANKINGS_TABLE_WITH_PROJECTIONS
        else -> TB2_RANKINGS_TABLE
    }
    table {
        entries = data
        onClick = { column, ranking ->
            when (column.title) {
                "Team" -> navigate {
                    toTeam(teamNumber = ranking.number, eventCode = eventCode)
                }

                "Mean Rank" -> navigate {
                    to = RoutePath(TEAM_PARAM)
                    params = buildReadonlyRecord {
                        this[TEAM_PARAM] = ranking.number.toString()
                    }
                    search = buildReadonlyRecord {
                        if (null != match) this[MATCH_SEARCH] = match
                    }
                }
            }
        }
    }
}
